import { InventoryCondition, InventoryEdition } from './inventory-values';

export const DECK_STATUS = {
  BUILDING: 'building',
  COMPLETE: 'complete',
  FOR_SALE: 'for_sale',
  ARCHIVED: 'archived',
} as const;

export const DECK_STATUS_LABELS: Record<DeckStatus, string> = {
  building: 'Em montagem',
  complete: 'Completo',
  for_sale: 'À venda',
  archived: 'Arquivado',
};

export type DeckStatus = (typeof DECK_STATUS)[keyof typeof DECK_STATUS];

export const DECK_SECTION = {
  MAIN: 'main',
  EXTRA: 'extra',
  SIDE: 'side',
} as const;

export type DeckSection = (typeof DECK_SECTION)[keyof typeof DECK_SECTION];

export type DeckImportFormat = 'omega' | 'text' | 'csv' | 'ydk';

export const DECK_KIND = { DECK: 'deck', SET: 'set' } as const;

export type DeckKind = (typeof DECK_KIND)[keyof typeof DECK_KIND];

export const DECK_PURPOSE = {
  SELLING: 'selling',
  OPT_BUYING: 'opt_buying',
  COLLECTION: 'collection',
} as const;

export type DeckPurpose = (typeof DECK_PURPOSE)[keyof typeof DECK_PURPOSE];

export const DECK_PURPOSE_LABELS: Record<DeckPurpose, string> = {
  opt_buying: 'Compra otimizada',
  selling: 'À venda',
  collection: 'Coleção',
};

export type DeckFormat = 'advanced' | 'genesys' | 'speed_duel';

export const DECK_FORMAT_LABELS: Record<DeckFormat, string> = {
  advanced: 'Avançado',
  genesys: 'Genesys',
  speed_duel: 'Speed Duel',
};

export interface DeckEntryInput {
  id?: string;
  cardId: string;
  printingId?: string;
  artworkId?: string;
  languageId?: string;
  condition?: InventoryCondition;
  quantity: number;
  ownedQuantity: number;
  section: DeckSection;
}

export interface OptimizedPurchaseEntryInput {
  cardId: string;
  quantity: number;
}

export interface DeckInput {
  expectedUpdatedAt?: string;
  gameId: string;
  name: string;
  description?: string;
  format?: DeckFormat;
  kind: DeckKind;
  purpose: DeckPurpose;
  coverCardId?: string;
  entries?: DeckEntryInput[];
  status: 'building' | 'archived';
  visibility: 'private' | 'public';
}

export type DeckUpdateInput = Omit<Partial<DeckInput>, 'status'> & {
  status?: 'building' | 'complete' | 'archived';
};

export interface Deck {
  id: string;
  name: string;
  description?: string;
  format?: DeckFormat;
  kind: DeckKind;
  purpose: DeckPurpose;
  coverCardId?: string;
  coverImageUrl?: string;
  status: DeckStatus;
  visibility: 'private' | 'public';
  game: { id: string; name: string; slug: string };
  entries: Array<
    DeckEntryInput & {
      id: string;
      cardName: string;
      imageUrl?: string;
    }
  >;
  unresolvedImports: Array<{
    id: string;
    sourceKey: string;
    rawLine: string;
    name: string;
    quantity: number;
    section: DeckSection;
  }>;
  offer?: {
    id: string;
    storeId: string;
    priceCents: number;
    quantity: number;
    status: 'active' | 'paused';
  };
  updatedAt: string;
}

export interface PublicDeck {
  id: string;
  name: string;
  description?: string;
  format?: DeckFormat;
  kind: DeckKind;
  purpose: DeckPurpose;
  coverCardId?: string;
  coverImageUrl?: string;
  status: DeckStatus;
  game: { id: string; name: string; slug: string };
  entries: Array<{
    cardId: string;
    cardName: string;
    imageUrl?: string;
    printingId?: string;
    quantity: number;
    section: DeckSection;
  }>;
  offer?: {
    id: string;
    priceCents: number;
    originalPriceCents?: number;
    quantity: number;
  };
  updatedAt: string;
}

export interface DeckBudget {
  calculatedAt: string;
  validUntil: string;
  considerCart: boolean;
  requiredUnits: number;
  coveredByOwnedUnits: number;
  coveredByCartUnits: number;
  remainingUnits: number;
  entries: Array<{
    entryId: string;
    cardId: string;
    cardName: string;
    imageUrl?: string;
    requestedQuantity: number;
    ownedQuantity: number;
    cartQuantity: number;
    remainingQuantity: number;
    status: 'owned' | 'in_cart' | 'partial' | 'missing' | 'covered';
  }>;
  stores: Array<{
    storeId: string;
    storeName: string;
    coveredUnits: number;
    coveredDistinctCards: number;
    coveragePercentage: number;
    totalCents: number;
    items: Array<{
      entryId: string;
      cardId: string;
      cardName: string;
      imageUrl?: string;
      inventoryItemId: string;
      printingId: string;
      printingCode: string;
      collectionName: string;
      rarityName: string;
      condition: InventoryCondition;
      languageName: string;
      edition: InventoryEdition | null;
      requiredQuantity: number;
      availableQuantity: number;
      selectedQuantity: number;
      unitPriceCents: number;
      subtotalCents: number;
      coverage: 'full' | 'partial';
    }>;
  }>;
}

export interface DeckImportMatch {
  sourceKey: string;
  rawLine: string;
  name: string;
  quantity: number;
  section: DeckSection;
  confidence: 'exact' | 'ambiguous' | 'not_found';
  candidates: Array<{ id: string; name: string; imageUrl?: string }>;
}

export interface DeckPublicationInput {
  priceCents: number;
  quantity: number;
  idempotencyKey: string;
  components: Array<{
    entryId: string;
    printingId: string;
    languageId: string;
    condition: InventoryCondition;
    edition?: InventoryEdition;
  }>;
}

export const DECK_COMPLETION_ERROR_CODE = {
  EMPTY: 'deck-completion-empty',
  UNRESOLVED: 'deck-completion-unresolved',
  NOT_OWNED: 'deck-completion-not-owned',
  INVALID_SECTIONS: 'deck-completion-invalid-sections',
} as const;

export const DECK_WRITE_ERROR_CODE = {
  VERSION_CONFLICT: 'deck-version-conflict',
} as const;
